import { legacyFormat, t } from "tbme/localization";
import { LightningElement, wire } from "lwc";
import {
  LabelTranslations,
  MultiLabelAdapter,
  MultiLabelAdapterConfig,
} from "tbme/localization";
import salesforceIconSrc from "./images/salesforce-icon.svg";
import { ApolloQueryResult, gql } from "@apollo/client";
import { QueryAdapter, QueryAdapterValue } from "tbme/queryAdapter";
import {
  GetAboutMyself,
  GetAboutMyself_chassis_profile,
} from "./gql/GetAboutMyself";
import { showToast } from "tds/toaster";
import { logger } from "tbme/logger";
import { sharedUrls } from "../localState/urls";
import { ABOUT_MYSELF_QUERY } from "./query";
import {
  countryOptions,
  getCountryLabel,
  getStateLabel,
} from "../../../shared/countryOptions";

export default class extends LightningElement {
  private user: GetAboutMyself_chassis_profile = undefined!;
  private visible: boolean = false;
  private displayProfileEditModal: boolean = false;
  private isSelf?: boolean;

  @wire(QueryAdapter, {
    query: ABOUT_MYSELF_QUERY,
  })
  private handleResult(result: QueryAdapterValue<GetAboutMyself>) {
    if (!result) {
      return;
    }
    const { data, loading, error, errors } = result;
    const chassis = data?.chassis;
    if (chassis) {
      const { profile, isSelf } = chassis;
      this.isSelf = isSelf;
      if (!profile || (!profile.isPublicProfile && !isSelf)) {
        this.visible = false;
        return;
      }

      this.user = profile;
      this.visible = true;
    }
  }

  private get profileUrl() {
    return this.user?.profileUrl;
  }

  private get showUrlComponent(): boolean {
    return !!this.user?.profileUrl;
  }

  private get displayUrl() {
    return this.user?.profileUrl?.replace("https://", "");
  }

  @wire<MultiLabelAdapterConfig>(MultiLabelAdapter, {
    labels: [
      "aboutMe.edit",
      "aboutMe.copy",
      "aboutMe.emptyAddShortBio",
      "aboutMe.hireMeOptIn",
      "aboutMe.hireMeOptOut",
    ],
  })
  private updateLabels({ aboutMe }: { aboutMe: LabelTranslations }) {
    this.labels = {
      ...aboutMe,
    };
  }
  private labels: LabelTranslations = undefined!;

  private get titleAtCompany() {
    if (this.user.role && this.user.company?.name) {
      return t("aboutMe.titleAtCompany", {
        title: this.user.role,
        company: this.user.company?.name,
      });
    }
    return this.user.company?.name;
  }

  private get fullName() {
    return `${this.user?.firstName} ${this.user?.lastName}`;
  }

  private get location(): string {
    const country = getCountryLabel(this.user.address?.country);
    const state = getStateLabel(
      this.user.address?.country,
      this.user.address?.state
    );

    if (country && state) {
      return `${state}, ${country}`;
    }

    return country || "";
  }

  private get salesforceIconSrc() {
    return salesforceIconSrc;
  }

  private async handleCopyClick() {
    const textarea = this.template.querySelector(
      ".slug-value"
    ) as HTMLTextAreaElement;

    try {
      await navigator.clipboard.writeText(textarea.value);
      showToast({
        message: t("aboutMe.copySuccess"),
        variant: "success",
      });
    } catch (e) {
      logger.error("Error copying text to clipboard", e);
    }
  }

  private handleEditClick() {
    this.displayProfileEditModal = true;
  }

  private handleProfileEditRequestClose() {
    this.displayProfileEditModal = false;
  }

  private get displayEmptyState(): boolean {
    return !!this.isSelf && !this.user.bio;
  }

  private get showHireMeButton() {
    return this.user.isAvailableForHire ?? false;
  }

  private get availableForHireLink() {
    const key: string =
      this.user.isAvailableForHire ?? false ? "hireMeOptOut" : "hireMeOptIn";
    return legacyFormat(this.labels[key], sharedUrls.availableForHire());
  }
}
